/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { useSettings } from '../hooks/useSettings';
import { toWhatsAppUrl } from '../api/settings';
import '../styles/Privacy.css';

function Privacy() {
  const [activeSection, setActiveSection] = useState('s1');
  const { settings } = useSettings();

  useEffect(() => {
    const sections = document.querySelectorAll('.policy-section');
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    sections.forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const tocItems = [
    { id: 's1', label: '1. Who we are' },
    { id: 's2', label: '2. Information we collect' },
    { id: 's3', label: '3. How we use your information' },
    { id: 's4', label: '4. Sharing your information' },
    { id: 's5', label: '5. Data storage & security' },
    { id: 's6', label: '6. Your rights' },
    { id: 's7', label: '7. Cookies & tracking' },
    { id: 's8', label: "8. Children's privacy" },
    { id: 's9', label: '9. Third-party links' },
    { id: 's10', label: '10. Changes to this policy' },
    { id: 's11', label: '11. Contact us' },
  ];

  return (
    <>
      <Header />

      {/* PAGE HEADER */}
      <div className="page-header">
        <div className="page-header-inner">
          <div className="page-eyebrow"><i className="ti ti-shield-check" style={{fontSize:'14px'}}></i> Legal</div>
          <h1>Privacy Policy</h1>
          <p style={{fontSize:'16px', color:'var(--text-muted)', maxWidth:'560px'}}>This policy explains how Sadguru Flower collects, uses, and protects your personal information when you use our app and services.</p>
          <div className="page-header-meta">
            <div className="meta-item"><i className="ti ti-calendar"></i> Effective date: June 1, 2026</div>
            <div className="meta-item"><i className="ti ti-refresh"></i> Last updated: June 1, 2026</div>
            <div className="meta-item"><i className="ti ti-map-pin"></i> Applicable in India</div>
          </div>
        </div>
      </div>

      {/* MAIN LAYOUT */}
      <div className="content-wrap">

        {/* SIDEBAR TOC */}
        <aside className="toc">
          <div className="toc-title">Contents</div>
          <ul className="toc-list">
            {tocItems.map(item => (
              <li key={item.id}>
                <a href={`#${item.id}`} className={activeSection === item.id ? 'active' : ''}>{item.label}</a>
              </li>
            ))}
          </ul>
          <div className="toc-divider"></div>
          <div className="toc-contact">
            Questions about this policy?<br /><br />
            <a href={`mailto:${settings.email}`}>{settings.email}</a><br />
            <a href={toWhatsAppUrl(settings.whatsappNumber)}>{settings.whatsappNumber}</a>
          </div>
        </aside>

        {/* POLICY CONTENT */}
        <main className="policy-content">

          <div className="policy-intro">
            <p><strong>Your privacy matters to us.</strong> At Sadguru Flower, we are committed to being transparent about the data we collect and how we use it. We collect only what is necessary to provide our flower delivery service and will never sell your personal information to third parties.</p>
          </div>

          {/* 1. WHO WE ARE */}
          <div className="policy-section" id="s1">
            <div className="section-number">Section 01</div>
            <h2><i className="ti ti-building-store"></i> Who we are</h2>
            <p>Sadguru Flower ("we", "us", or "our") is a premium online floral boutique operating through our mobile application available on the Google Play Store and Apple App Store. We deliver farm-fresh flowers, bouquets, premium gifts, and handcrafted wedding varmalas across Gujarat, India.</p>
            <div className="info-card">
              <div className="info-card-title"><i className="ti ti-map-pin"></i> Business details</div>
              <p>
                <strong>Business name:</strong> Sadguru Flower<br />
                <strong>Service area:</strong> Morbi, Rajkot, Jamnagar, Halvad (Gujarat, India)<br />
                <strong>Contact:</strong> {settings.email} &nbsp;|&nbsp; {settings.contactNumber}
              </p>
            </div>
            <p>By downloading and using the Sadguru Flower app, you agree to the collection and use of information as described in this Privacy Policy. If you do not agree, please do not use our app or services.</p>
          </div>

          {/* 2. INFORMATION WE COLLECT */}
          <div className="policy-section" id="s2">
            <div className="section-number">Section 02</div>
            <h2><i className="ti ti-database"></i> Information we collect</h2>
            <p>We collect information you provide directly to us, as well as certain information automatically when you use our app.</p>
            <h3>Information you provide</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data type</th>
                  <th>Examples</th>
                  <th>When collected</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Phone number</strong></td>
                  <td>Indian mobile number for OTP login</td>
                  <td>At registration / login</td>
                </tr>
                <tr>
                  <td><strong>Delivery addresses</strong></td>
                  <td>Home, Work, or custom addresses in Gujarat</td>
                  <td>When you add an address</td>
                </tr>
                <tr>
                  <td><strong>Order details</strong></td>
                  <td>Products ordered, quantity, special instructions</td>
                  <td>When you place an order</td>
                </tr>
                <tr>
                  <td><strong>Payment information</strong></td>
                  <td>Payment method selected (UPI/COD). We do not store card or UPI credentials.</td>
                  <td>At checkout</td>
                </tr>
                <tr>
                  <td><strong>Reviews &amp; ratings</strong></td>
                  <td>Star ratings and written reviews after delivery</td>
                  <td>When you submit a review</td>
                </tr>
              </tbody>
            </table>
            <h3>Information collected automatically</h3>
            <ul className="policy-list">
              <li>Device information such as device model, operating system version, and unique device identifiers</li>
              <li>App usage data such as features accessed, pages viewed, and time spent in the app</li>
              <li>Approximate location data (city-level) to confirm delivery serviceability — only when you place an order</li>
              <li>Crash reports and error logs to help us improve app stability</li>
            </ul>
            <div className="highlight-box">
              <p><strong>We do not collect:</strong> Precise GPS location in the background, contacts, camera or microphone access (unless you choose to upload a photo), or any biometric data.</p>
            </div>
          </div>

          {/* 3. HOW WE USE YOUR INFORMATION */}
          <div className="policy-section" id="s3">
            <div className="section-number">Section 03</div>
            <h2><i className="ti ti-settings"></i> How we use your information</h2>
            <p>We use the information we collect solely to provide, improve, and personalise our flower delivery service. Specifically:</p>
            <ul className="policy-list">
              <li><strong>To process and deliver your orders</strong> — your phone number and address are shared with our delivery partner assigned to your order</li>
              <li><strong>To send order updates</strong> — SMS or in-app notifications about your order status (confirmed, packed, shipped, out for delivery)</li>
              <li><strong>To enable OTP-based login</strong> — your phone number is used to send a one-time password for secure, passwordless authentication</li>
              <li><strong>To personalise your experience</strong> — show relevant offers, saved addresses, and order history within the app</li>
              <li><strong>To improve our service</strong> — analyse usage patterns and crash reports to fix bugs and improve app performance</li>
              <li><strong>To send promotional offers</strong> — seasonal sales, discount codes, and new product announcements (you can opt out at any time)</li>
              <li><strong>To respond to customer support requests</strong> — when you contact us via WhatsApp, phone, or email</li>
            </ul>
            <p>We will never use your data for purposes unrelated to the above without your explicit consent.</p>
          </div>

          {/* 4. SHARING YOUR INFORMATION */}
          <div className="policy-section" id="s4">
            <div className="section-number">Section 04</div>
            <h2><i className="ti ti-share"></i> Sharing your information</h2>
            <p>We do not sell, rent, or trade your personal information. We share your data only in the following limited circumstances:</p>
            <h3>Delivery partners</h3>
            <p>Your name, phone number, and delivery address are shared with our assigned delivery partner solely to complete your order. They are not authorised to use this information for any other purpose.</p>
            <h3>Payment processors</h3>
            <p>When you pay via UPI, your transaction is processed by the respective UPI app (GPay, PhonePe, or Paytm). We do not receive or store your UPI ID, bank account details, or card information. Cash on Delivery (COD) orders require no payment data sharing.</p>
            <h3>Service providers</h3>
            <p>We may use trusted third-party services for SMS delivery (OTP), push notifications, and app analytics. These providers are contractually obligated to keep your data confidential and may not use it for their own purposes.</p>
            <h3>Legal requirements</h3>
            <p>We may disclose your information if required by law, court order, or government authority, or to protect the rights, safety, and security of Sadguru Flower and our users.</p>
            <div className="info-card">
              <div className="info-card-title"><i className="ti ti-shield"></i> Our commitment</div>
              <p>We will never sell your personal information to advertisers, data brokers, or any third party for commercial purposes.</p>
            </div>
          </div>

          {/* 5. DATA STORAGE & SECURITY */}
          <div className="policy-section" id="s5">
            <div className="section-number">Section 05</div>
            <h2><i className="ti ti-lock"></i> Data storage &amp; security</h2>
            <p>Your data is stored on secure servers located in India. We implement industry-standard security measures to protect your information from unauthorised access, loss, or misuse, including:</p>
            <ul className="policy-list">
              <li>Encrypted data transmission using HTTPS/TLS for all app communications</li>
              <li>OTP-based authentication — no passwords are stored</li>
              <li>Access to personal data is restricted to authorised personnel only</li>
              <li>Regular security reviews of our systems and infrastructure</li>
            </ul>
            <h3>Data retention</h3>
            <p>We retain your personal information for as long as your account is active or as needed to provide our services. You may request deletion of your account and associated data at any time by contacting us. We will delete your data within 30 days of a valid request, except where we are required to retain it by law.</p>
            <div className="highlight-box">
              <p><strong>Note:</strong> While we take all reasonable steps to protect your data, no method of transmission over the internet is 100% secure. If you suspect unauthorised access to your account, contact us immediately at <strong>support@sadguruflower.com</strong>.</p>
            </div>
          </div>

          {/* 6. YOUR RIGHTS */}
          <div className="policy-section" id="s6">
            <div className="section-number">Section 06</div>
            <h2><i className="ti ti-user-check"></i> Your rights</h2>
            <p>As a user of our app, you have the following rights regarding your personal information:</p>
            <ul className="policy-list">
              <li><strong>Right to access</strong> — you can request a copy of the personal data we hold about you</li>
              <li><strong>Right to correction</strong> — you can update your phone number or delivery addresses directly in the app at any time</li>
              <li><strong>Right to deletion</strong> — you can request that we delete your account and all associated personal data</li>
              <li><strong>Right to opt out of marketing</strong> — you can unsubscribe from promotional messages by contacting us or adjusting your notification settings in the app</li>
              <li><strong>Right to data portability</strong> — you can request your order history and account data in a readable format</li>
            </ul>
            <p>To exercise any of these rights, contact us at <a href={`mailto:${settings.email}`}>{settings.email}</a> or WhatsApp us at <a href={toWhatsAppUrl(settings.whatsappNumber)}>{settings.whatsappNumber}</a>. We will respond within 7 business days.</p>
          </div>

          {/* 7. COOKIES & TRACKING */}
          <div className="policy-section" id="s7">
            <div className="section-number">Section 07</div>
            <h2><i className="ti ti-cookie"></i> Cookies &amp; tracking</h2>
            <p>The Sadguru Flower mobile app does not use browser cookies. However, we may use the following technologies within the app:</p>
            <ul className="policy-list">
              <li><strong>Analytics SDKs</strong> — to understand how users navigate the app and identify areas for improvement (e.g., screen views, button taps). This data is aggregated and anonymised.</li>
              <li><strong>Crash reporting tools</strong> — to detect and fix technical errors automatically</li>
              <li><strong>Push notification tokens</strong> — a device token generated by your phone's OS to deliver in-app and push notifications. You can disable push notifications at any time in your phone settings.</li>
            </ul>
            <p>If you access our website, standard web analytics may collect your browser type, approximate location, and pages visited. This data is used only in aggregate form.</p>
          </div>

          {/* 8. CHILDREN'S PRIVACY */}
          <div className="policy-section" id="s8">
            <div className="section-number">Section 08</div>
            <h2><i className="ti ti-users"></i> Children's privacy</h2>
            <p>The Sadguru Flower app is intended for users who are 13 years of age or older. We do not knowingly collect personal information from children under 13. If we discover that we have inadvertently collected such information, we will delete it promptly.</p>
            <p>If you are a parent or guardian and believe your child has provided us with personal information, please contact us at <a href="mailto:support@sadguruflower.com">support@sadguruflower.com</a> and we will take immediate action.</p>
          </div>

          {/* 9. THIRD-PARTY LINKS */}
          <div className="policy-section" id="s9">
            <div className="section-number">Section 09</div>
            <h2><i className="ti ti-external-link"></i> Third-party links</h2>
            <p>Our app may contain links to third-party services such as UPI payment apps (GPay, PhonePe, Paytm) or social media platforms. Once you leave our app and visit a third-party service, this Privacy Policy no longer applies.</p>
            <p>We encourage you to review the privacy policies of any third-party services you use. Sadguru Flower is not responsible for the privacy practices or content of third-party websites or applications.</p>
          </div>

          {/* 10. CHANGES TO POLICY */}
          <div className="policy-section" id="s10">
            <div className="section-number">Section 10</div>
            <h2><i className="ti ti-refresh"></i> Changes to this policy</h2>
            <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or for other operational reasons. When we make material changes, we will:</p>
            <ul className="policy-list">
              <li>Update the "Last updated" date at the top of this page</li>
              <li>Send an in-app notification or push notification informing you of the update</li>
              <li>For significant changes, ask for your renewed consent where required by law</li>
            </ul>
            <p>We encourage you to review this policy periodically. Your continued use of the Sadguru Flower app after any changes constitutes your acceptance of the updated policy.</p>
          </div>

          {/* 11. CONTACT US */}
          <div className="policy-section" id="s11">
            <div className="section-number">Section 11</div>
            <h2><i className="ti ti-headset"></i> Contact us</h2>
            <p>If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your personal data, our team is here to help. We respond to all privacy-related inquiries within 7 business days.</p>
          </div>

          {/* CONTACT CARD */}
          <div className="contact-section">
            <h2>Get in touch</h2>
            <p>Our customer care team is available 7 days a week. Reach us through any of the channels below for privacy-related questions or data requests.</p>
            <div className="contact-methods">
              <a className="contact-method" href={`mailto:${settings.email}`}>
                <div className="contact-method-icon"><i className="ti ti-mail"></i></div>
                <div className="contact-method-txt">
                  <div className="cm1">Email us at</div>
                  <div className="cm2">{settings.email}</div>
                </div>
              </a>
              <a className="contact-method" href={toWhatsAppUrl(settings.whatsappNumber)}>
                <div className="contact-method-icon"><i className="ti ti-brand-whatsapp"></i></div>
                <div className="contact-method-txt">
                  <div className="cm1">WhatsApp / Call</div>
                  <div className="cm2">{settings.whatsappNumber}</div>
                </div>
              </a>
            </div>
          </div>

        </main>
      </div>

      {/* FOOTER */}
      <footer className="pr-footer">
        <p>© 2026 Sadguru Flower. All rights reserved.</p>
        <div className="pr-footer-links">
          <Link to="/">Home</Link>
          <a href="#">Terms of service</a>
          <a href="#">Refund policy</a>
        </div>
      </footer>
    </>
  );
}

export default Privacy;
